import {LOCAL_STORAGE_PLACE} from "../components/constants/local-storage-constants";
import GeocoderResult = google.maps.GeocoderResult;

/**
 * Saves a geocoded place to local storage
 */
export const saveGeocodedPlaceToLocalStorage = (place: GeocoderResult | null | undefined): void => {
    // Clear storage if place is null/undefined or invalid
    if (!place || !place.geometry || !place.geometry.location) {
        localStorage.removeItem(LOCAL_STORAGE_PLACE);
        if (place) {
            console.warn('Cannot save invalid place to local storage (missing geometry.location):', place);
        }
        return;
    }

    // Validate that location has the required methods
    const location = place.geometry.location;
    if (typeof location.lat !== 'function' || typeof location.lng !== 'function') {
        localStorage.removeItem(LOCAL_STORAGE_PLACE);
        console.error('Invalid place.geometry.location (missing lat/lng methods):', place);
        return;
    }

    try {
        const placeData = {
            place_id: place.place_id,
            formatted_address: place.formatted_address,
            address_components: place.address_components,
            geometry: {
                location: {
                    lat: location.lat(),
                    lng: location.lng()
                },
                location_type: place.geometry.location_type,
                viewport: place.geometry.viewport ? {
                    northeast: {
                        lat: place.geometry.viewport.getNorthEast().lat(),
                        lng: place.geometry.viewport.getNorthEast().lng()
                    },
                    southwest: {
                        lat: place.geometry.viewport.getSouthWest().lat(),
                        lng: place.geometry.viewport.getSouthWest().lng()
                    }
                } : undefined
            },
            types: place.types
        };
        localStorage.setItem(LOCAL_STORAGE_PLACE, JSON.stringify(placeData));
    } catch (error) {
        // If serialization fails, clear the key to avoid stale/corrupt data
        localStorage.removeItem(LOCAL_STORAGE_PLACE);
        console.error('Failed to save place to local storage:', error, place);
    }
};

/**
 * Reconstructs a GeocoderResult from stored data in local storage
 * @returns undefined - when reconstruction fails (e.g. due to missing geometry or SDK not loaded)
 */
export const reconstructGeocoderResult = (storedPlace: any): GeocoderResult | undefined => {
    if (!storedPlace || !storedPlace.geometry || !storedPlace.geometry.location) {
        return undefined;
    }

  // Guard: SDK may not be loaded if called outside <APIProvider>
  if (!window.google?.maps?.LatLng) {
    console.error('Google Maps SDK not loaded — cannot reconstruct GeocoderResult');
    return undefined;
  }

    try {
        // Reconstruct LatLng objects
        const lat = storedPlace.geometry.location.lat;
        const lng = storedPlace.geometry.location.lng;
        const location = new window.google.maps.LatLng(lat, lng);

        // Reconstruct viewport if present
        let viewport: google.maps.LatLngBounds | undefined;
        if (storedPlace.geometry.viewport) {
            const ne = storedPlace.geometry.viewport.northeast;
            const sw = storedPlace.geometry.viewport.southwest;
            viewport = new window.google.maps.LatLngBounds(
                new window.google.maps.LatLng(sw.lat, sw.lng),
                new window.google.maps.LatLng(ne.lat, ne.lng)
            );
        }

        return {
            ...storedPlace,
            geometry: {
                ...storedPlace.geometry,
                location,
                viewport
            }
        } as GeocoderResult;
    } catch (error) {
        console.error('Failed to reconstruct geocoder result:', error);
        return undefined;
    }
};

/**
 * Retrieves and reconstructs a geocoded place from local storage
 */
export const getGeocodedPlaceFromLocalStorage = (): GeocoderResult | undefined => {
    try {
        const storedPlaceJson = localStorage.getItem(LOCAL_STORAGE_PLACE);
        if (!storedPlaceJson) {
            return undefined;
        }

        const storedPlace = JSON.parse(storedPlaceJson);
        const reconstructed = reconstructGeocoderResult(storedPlace);

        // If reconstruction failed, clear the corrupted data
        if (!reconstructed) {
            localStorage.removeItem(LOCAL_STORAGE_PLACE);
            console.warn('Cleared corrupted place data from local storage');
        }

        return reconstructed;
    } catch (error) {
        // If parsing fails, clear the corrupted data
        localStorage.removeItem(LOCAL_STORAGE_PLACE);
        console.error('Failed to retrieve place from local storage, cleared corrupted data:', error);
        return undefined;
    }
};

